import React from "react";
import { useNavigate } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import { SessionData } from "@/modules/eventRegistration/features/EventDirectory/api/fetchEventDetails/types";
import {
  formatDateRangeInTimeZone,
  formatTimeRangeInTimeZone,
} from "@/modules/eventRegistration/features/EventDirectory/utils/dateUtils";
import { groupSessionDays } from "@/modules/eventRegistration/shared/utils/groupSessionDays";
import { buildLegacyEventUrl } from "@/modules/eventRegistration/utils/buildLegacyEventUrl";
import { useAppSelector } from "@/app/hooks";
import { RootState } from "@/app/rootReducer";

interface EventDetailsSessionListProps {
  sessions: SessionData[] | null | undefined;
  legacyCourseId?: number | null;
}

const EventDetailsSessionList: React.FC<EventDetailsSessionListProps> = ({
  sessions,
  legacyCourseId = null,
}) => {
  const navigate = useNavigate();
  const memberPortalCompanyId = useAppSelector(
    (state: RootState) =>
      state.userAppSettings.userAppSettings?.memberPortalCompanyId ?? null,
  );

  if (!sessions || sessions.length === 0) {
    return null;
  }

  return (
    <div>
      <h2 className="text-base font-normal text-gray-900 dark:text-white mb-3">
        Sessions
      </h2>
      <div className="space-y-3">
        {sessions.map((session) => {
          // Broken-out session days render as stacked grouped lines (each
          // group with its own time). Legacy sessions without per-day data
          // fall back to the single overall date/time range.
          const sessionDayGroups = groupSessionDays(
            session.sessionDays,
            session.timezoneIdentifier || null,
            session.timezoneShortName || null,
          );
          const dateRange = formatDateRangeInTimeZone(
            session.startDate || null,
            session.endDate || null,
            session.timezoneIdentifier || null,
          );
          const timeRange = formatTimeRangeInTimeZone(
            session.startDate || null,
            session.endDate || null,
            session.timezoneIdentifier || null,
          );
          const mainTimeDisplay = session.timezoneShortName
            ? `${timeRange} (${session.timezoneShortName})`
            : timeRange;

          const seats = session.availableSeats;
          let availabilityText = "";
          let availabilityClass = "text-xs text-gray-400 dark:text-gray-500";
          if (
            !session.isFinalized &&
            !session.isCancelled &&
            typeof seats === "number"
          ) {
            if (seats > 0) {
              availabilityText = `${seats} seats left`;
            } else {
              availabilityText = "Waitlist only";
              availabilityClass = "text-xs text-amber-600 dark:text-amber-400";
            }
          }

          let venueText = "";
          if (session.venue) {
            const parts = [session.venue.name];
            if (session.venue.city) {
              parts.push(
                `${session.venue.city}, ${session.venue.state || ""}`.trim(),
              );
            }
            venueText = parts.filter(Boolean).join(" · ");
          } else if (session.isVirtualOnly) {
            venueText = "Virtual only";
          }

          const isClickable = !session.isFinalized && !session.isCancelled;
          const handleRegister = () => {
            if (session.redirectToLegacy) {
              // Legacy session — open Member Portal's SelectUser page for
              // THIS specific session (clicked one, not "next upcoming")
              // in a new tab. If MP didn't return a registration URL for
              // this session at sync time, buildLegacyEventUrl falls
              // back to the course-overview page using legacyCourseId.
              window.open(
                buildLegacyEventUrl(
                  "directory",
                  legacyCourseId,
                  session.memberPortalRegistrationUrl,
                  memberPortalCompanyId,
                ),
                "_blank",
                "noopener,noreferrer",
              );
              return;
            }
            navigate(`/event-registration/events/${session.uuid}/register`);
          };

          return (
            <div
              className={`group bg-white dark:bg-slate-800 rounded-lg border border-gray-200 dark:border-slate-700 p-4 ${
                isClickable
                  ? "cursor-pointer hover:border-gray-300 dark:hover:border-slate-600 transition-colors"
                  : "opacity-60"
              }`}
              key={session.id}
              onClick={isClickable ? handleRegister : undefined}
            >
              {/* Session name */}
              <div className="text-sm font-medium text-gray-900 dark:text-white">
                {session.name || "Session"}
              </div>

              {/* Date, time, timezone — stacked per session-day group when
                  broken-out days are present, else the single overall range. */}
              {sessionDayGroups.length > 0 ? (
                <div className="mt-1 space-y-0.5">
                  {sessionDayGroups.map((group, i) => (
                    <div
                      className="text-sm text-gray-500 dark:text-gray-400"
                      key={`${group.dateLabel}-${i}`}
                    >
                      {group.dateLabel} · {group.timeLabel}
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-sm text-gray-500 dark:text-gray-400 mt-1">
                  {dateRange} · {mainTimeDisplay}
                </div>
              )}

              {/* Venue */}
              {venueText && (
                <div className="text-sm text-gray-500 dark:text-gray-400 mt-0.5">
                  {venueText}
                </div>
              )}

              {/* Action row */}
              <div className="flex items-center justify-between mt-3 pt-3 border-t border-gray-100 dark:border-slate-700/50">
                {isClickable ? (
                  <>
                    <span className={availabilityClass}>
                      {availabilityText}
                    </span>
                    <Button
                      onClick={(e) => {
                        e.stopPropagation();
                        handleRegister();
                      }}
                      size="sm"
                    >
                      Register
                      <ArrowRight className="ml-1.5 h-3.5 w-3.5" />
                    </Button>
                  </>
                ) : (
                  <>
                    <span />
                    <span className="text-xs text-gray-400 dark:text-gray-500">
                      {session.isCancelled ? "Cancelled" : "Completed"}
                    </span>
                  </>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default EventDetailsSessionList;
